import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LangContext } from '../../contexts/langContext';
import { gates_dictionary } from '../../static/gates_dictionary';
import { planets_dictionary } from '../../static/planets_dictionary';
import { theme } from '../UiKit/Colors';
import { DiamondRadarChart, DIAMOND_RADAR } from '../UiKit/DiamondRadarChart';
import { Gate } from '../UiKit/Gate';
import { RadioItem } from '../UiKit/RadioItem';
import { AstroIcon } from '../UiKit/AstroIcon';
import { Switch } from '../UiKit/Switch';
import { DescriptionIco } from '../UiKit/DescriptionIco';
import { TooltipH } from '../UiKit/TooltipH';
import { prepareGates } from '../utilsDesign';
import { useEduGroupBy, useEduGroupByOptional } from './EduGroupByContext';
import '../css/diamond-radar.css';

const CIRCUIT_AXES = [
  { id: 'individual', angle: 0 },
  { id: 'tribal', angle: 120 },
  { id: 'collective', angle: 240 },
];

/** des-only → mixed → prs-only (clockwise from top). */
const ACTIVATION_AXES = [
  { id: 'des', angle: 0 },
  { id: 'des,prs', angle: 120 },
  { id: 'prs', angle: 240 },
];

const CIRCUIT_COLORS = {
  individual: '#2c3e50',
  tribal: '#a7302c',
  collective: '#3a7a4a',
  none: '#d4d0c8',
};

const ACTIVATION_COLORS = {
  des: theme.designColor,
  'des,prs': '#8e6767',
  prs: theme.personalityColor,
  none: '#d4d0c8',
};

const clampRadar = (n) => Math.min(Math.max(n, 0), DIAMOND_RADAR.RINGS);

const activationBucket = (activations = []) => {
  const labels = [...new Set(activations)];
  const hasDes = labels.includes('des');
  const hasPrs = labels.includes('prs');
  if (hasDes && hasPrs) return 'des,prs';
  if (hasDes) return 'des';
  if (hasPrs) return 'prs';
  return null;
};

/** Diamond radar + group switches: circuits or activations. */
export const GatesRadarCharts = React.memo(({ design }) => {
  const { $ui } = useContext(LangContext);
  const { page, setPage } = useEduGroupBy('circuit');

  const { circuitCounts, activationCounts } = useMemo(() => {
    const circuits = { individual: 0, tribal: 0, collective: 0 };
    const activations = { des: 0, 'des,prs': 0, prs: 0 };
    if (!design?.gates?.length) return { circuitCounts: circuits, activationCounts: activations };

    const byGate = prepareGates(design.gates);
    Object.keys(byGate).forEach((gateId) => {
      const meta = gates_dictionary[gateId];
      (meta?.circuit || []).forEach((one) => {
        if (circuits[one] != null) circuits[one] += 1;
      });
      const bucket = activationBucket((byGate[gateId] || []).map((one) => one.label));
      if (bucket) activations[bucket] += 1;
    });
    return { circuitCounts: circuits, activationCounts: activations };
  }, [design?.gates]);

  const total = activationCounts.des + activationCounts['des,prs'] + activationCounts.prs;
  if (!total) return null;

  const isCircuit = page === 'circuit';
  const axes = isCircuit ? CIRCUIT_AXES : ACTIVATION_AXES;
  const counts = isCircuit ? circuitCounts : activationCounts;
  const colors = isCircuit ? CIRCUIT_COLORS : ACTIVATION_COLORS;

  return (
    <div className="channels-radars channels-radars--switched">
      <div className="channels-radar">
        <DiamondRadarChart
          axes={axes}
          values={Object.fromEntries(axes.map(({ id }) => [id, clampRadar(counts[id])]))}
          colors={axes.map(({ id }) => (counts[id] > 0 ? colors[id] : colors.none))}
          ariaLabel={axes.map(({ id }) => `${$ui(id)}: ${counts[id]}`).join(', ')}
          labels={axes.map(({ id }) => ({
            id,
            content: $ui(id),
            title: $ui(id),
          }))}
          renderLabel={({ count, content, title }) => (
            <TooltipH title={<div className="_fv _fgap02"><b>{title}</b><span>{count}</span></div>}>
              <span className="diamond-radar-label-emoji diamond-radar-label-text">{content}</span>
            </TooltipH>
          )}
        />
      </div>
      <div className="x_edu_select0 channels-radar-switches">
        <div className="x_choose_list">
          <RadioItem active={page === 'circuit'} onClick={() => setPage('circuit')}>
            {$ui('group_by_circuits')}
          </RadioItem>
          <RadioItem active={page === 'activations'} onClick={() => setPage('activations')}>
            {$ui('group_by_activations')}
          </RadioItem>
        </div>
      </div>
    </div>
  );
});


export const Gates = React.memo((props) => {

  const context = { ...useContext(LangContext) };
  const $ui = context.$ui;

  const {
    advanced,
    design = {},
    videos = 'inline'
  } = { ...props }


  const [openOnly, setOpenOnly] = useState(true)
  const [filter, setFilterInner] = useState()

  const setFilter = (value) => {
    if (filterByType(gatesActivations, "circuit", value).length) {
      setFilterInner(value)
    }
  }


  const gatesActivations = prepareGates(design['gates']);

  // const channels = gatherUserChannels(design.gates)
  const channelsGates = design.channels.map(one => one.gates).flat()


  const myGates = []
  Object.keys(gatesActivations)
    .filter(one => {
      return openOnly ? !channelsGates.includes(parseInt(one)) : true
    })
    .forEach(gateId => {
      myGates.push({
        ...gates_dictionary[gateId],
        activations: [...new Set(gatesActivations[gateId].map(one => one.label))],
        activatedWIth: gatesActivations[gateId]
      })
    })



  const filterByType = (array = [], what = '', value = []) => {
    if (!value) return array;
    return array.filter(oneChannel => {
      return oneChannel[what].filter(oneValue => {
        return value.includes(oneValue)
      }).length === oneChannel[what].length && oneChannel[what].length === value.length;
    })
  }


  const groupFromTitle = useEduGroupByOptional();
  const { page, setPage } = useEduGroupBy('circuit');

  const sorts = {
    'circuit': [['individual'], ['tribal'], ['collective']],
    'type': [['projected'], ['generated'], ['manifested']],
    'activations': [['des'], ['des', 'prs'], ['prs']],
  }

  const [showActivations, setShowActivations] = useState(false);

  return (
    <>

      <div className="x_edu_content x_edu_content--gates">

      <div className="_fv _fgap0 _f100">

        <div className="x_edu_select0">
          {!groupFromTitle && <div className="x_choose_list">
            <RadioItem
              active={page === 'circuit'}
              onClick={() => setPage('circuit')}
            >
              {$ui('group_by_circuits')}
            </RadioItem>

            <RadioItem
              active={page === 'activations'}
              onClick={() => setPage('activations')}
            >
              {$ui('group_by_activations')}
            </RadioItem>
          </div>}
            <div className="x_edu_select_right mobile_v_no">
            <Switch
              size='xs'
              label={$ui('edu_show_openonly')}
              active={openOnly}
              handle={() => setOpenOnly(prev => !prev)}
            />
          </div>
        </div>


        <div className="x_edu_activations_table">


          {sorts[page].map(oneCircuit => <div
            key={oneCircuit}
            className="x_edu_activations_column"
          >

            <div className='x_edu_h2'>
              {$ui(oneCircuit)}
              {(() => {
                const descKey = `${oneCircuit.toString()}_gate_desc`;
                return $ui(descKey) !== descKey && <DescriptionIco>{$ui(descKey)}</DescriptionIco>
              })()}
            </div>


            <div className="x_edu_channels">
              {/* <div className="x_edu_content_label">Gates</div> */}
              {filterByType(myGates, page, oneCircuit)
                .map(oneGate => {
                  const oneGateId = parseInt(oneGate.gate);
                  return (<Gate
                    showBodygraphHint={true}
                    page={page}
                    advanced={advanced}
                    key={`gate_${oneGateId}_${oneCircuit}`}
                    label={false}
                    design={design}
                    gateNumber={oneGateId}
                    {...oneGate}
                    showActivations={showActivations}
                    // expert={expert}
                    videos={videos}
                  />)
                })}


              {!Boolean(filterByType(myGates, 'circuit', oneCircuit).length) && <div className='x_edu_no_activations'>{$ui('no_gates')}</div>}


            </div>
          </div>)}

        </div>
      </div>


      </div>

    </>
  )
})





export const GatePlanet = React.memo(({
  gate,
  design,
  line = true,
  color = false,
  tone = false,
  inLine,
  inColor,
  inTone
}) => {

  const context = { ...useContext(LangContext) }
  const $ui = context.$ui;
  const $planet = context.$planet;

  return (<>

    <div className={`x_edu_gate_planet ${gate['label'] === 'prs' ? 'prs' : 'des'}`}
      style={{ marginBottom: '-0.4em', position: 'relative' }}
    >


      <Planet
        planet={gate['planet']}
        label={gate['label']}
      />

      <div className="">
        {`${$planet(gate['planet'], 'title')} ${inLine ? $ui('in_line') : ''} ${gate['line']} ${inColor ? $ui('in_color') : ''} ${color ? gate['color'] : ''}  ${inTone ? $ui('in_tone') : ''} ${tone ? gate['tone'] : ''} `}
      </div>

    </div>
    {/* <div className="x_planet_gates">
      <GateInfo design={design} gate={gate} design={design} />
    </div> */}

  </>
  )
})



export const Planet = React.memo(({ planet = 'Sun', label }) => {

  const context = { ...useContext(LangContext) }
  const $ui = context.$ui;
  const $planet = context.$planet;

  return (
    <>
    {planets_dictionary[planet] && <div className="x_bodygraph_pl">
      <AstroIcon
        name={planet}
        width="100%"
        height="100%"
        color={label === 'des' ? theme.designColor : theme.personalityColor}
      />
    </div>}
    </>
  )
})



const CSStitleLine = styled.div`
width: 100%;
  display: flex;
  justify-content: stretch;
`
export const CSSplanetLine = styled.div`


`

export const CSSplanet = styled.div`
  color: ${({ label }) => label === 'prs' ? 'black' : 'red'};
  display: flex;
  grid-column-gap: 0em;
  align-items: center;
  // font-size: 14px;
  font-size: 1em;
`


